/**
 * What the feed did, kept where the shell can read it cheaply.
 *
 * This is a separate module for a bundling reason worth stating, because the
 * alternative looks tidier and costs most of a megabyte.
 *
 * The provenance line is rendered by the shell, which is mounted for the whole
 * session — including on the sign-in screen and on every platform screen that
 * has nothing to do with the boards. If it read the report straight out of
 * `merge.ts`, it would pull in `adapt.ts`, `project.ts` and through them the
 * demo's 181 reference tables, and every one of those would land in the entry
 * chunk. The measurement is not subtle: the entry bundle went from 0.60 MB to
 * 1.44 MB when this file did not exist.
 *
 * So the counts live here, in a module that imports nothing, and `merge.ts`
 * writes to it. The shell reads a few numbers; the domain stays in the chunk
 * that draws it.
 */

/** What a merge did, so the shell can say it rather than the reader having to guess. */
export interface FeedReport {
  workOrders: { fed: number; unlocated: number; cancelled: number; replaced: number };
  inspections: { fed: number; notCompleted: number };
  documents: { fed: number };
}

export const emptyReport = (): FeedReport => ({
  workOrders: { fed: 0, unlocated: 0, cancelled: 0, replaced: 0 },
  inspections: { fed: 0, notCompleted: 0 },
  documents: { fed: 0 },
});

let report = emptyReport();

const listeners = new Set<() => void>();

/** What the last merge did. */
export const feedReport = (): FeedReport => report;

/** Say the feed is not running. Called by the shell under the mock adapter. */
export function clearFeed(): void {
  setFeedReport(emptyReport());
}

export function setFeedReport(next: FeedReport): void {
  report = next;
  for (const fn of listeners) fn();
}

/**
 * Subscribe to changes. Shaped for `useSyncExternalStore`, which is how the
 * shell hears about a merge that happened inside a lazily loaded chunk it has
 * no other connection to.
 */
export function subscribeFeed(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
